import { inject, Injectable } from '@angular/core';
import { assert } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import { defer, from, of, switchMap } from 'rxjs';
import { TokenManagementService } from '../../../cross-team-shared/misc-services/token-management.service';
import { WorkspaceConfigService } from '../../../shared/misc-services/workspace-config.service';
import { StepResource } from '../../../shared/resources/step-resource.service';
import { UpsertAnswerOperation } from '../../../team-apis/otterdam/annotation-operations/answer-operations';
import {
  getOutputById,
  isAnnotationOutput,
} from '../../h3ml/dsl/sensor-fusion/sensor-fusion-output.utils';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { PersistenceService } from '../../shared/misc-services/persistence.service';
import {
  annotationQueueName,
  AnnotationResource,
} from '../../shared/resources/annotation-resource.service';
import { StopWorkSource } from '../stop-work-button/stop-work.service';
import { TimerService } from '../timer-badge/timer.service';
import { SubmissionLoggingService } from './submission-logging.service';
import { SubmitTaskService } from './submit-task.service';

@Injectable()
export class PersistenceManager extends InjectableSuperclass {
  private store = inject(TaskWorkspaceStore);
  private persistenceService = inject(PersistenceService);
  private annotationResource = inject(AnnotationResource);
  private submitTaskService = inject(SubmitTaskService);
  private submissionLoggingService = inject(SubmissionLoggingService);
  private stepResource = inject(StepResource);
  private timerService = inject(TimerService);
  private workspaceConfigService = inject(WorkspaceConfigService);
  private tokenService = inject(TokenManagementService);

  submit(qaGroupId: string) {
    this.submissionLoggingService.updateStep('submissionStarted');
    this.updateMetadata$();
    this.upsertAnswerForOutputs$();
    return this.submit$(qaGroupId);
  }

  stopWork(source: StopWorkSource) {
    const { projectId, stepId } = this.workspaceConfigService;
    assert(stepId, 'stepId should always defined in production workspace');

    if (source !== 'submit') {
      this.updateMetadata$();
    }

    const token$ = this.tokenService.getToken$('access_token');
    const stopWork$ = token$.pipe(
      switchMap((token) =>
        this.stepResource.stopWork$(projectId, stepId, token),
      ),
    );
    return this.persistenceService.enqueue$(stopWork$, annotationQueueName);
  }

  private upsertAnswerForOutputs$() {
    if (this.store.isUsingGenAi()) {
      return of(undefined);
    }

    const { project } = this.store.state();
    const operations = project.outputs
      .filter(isAnnotationOutput)
      .map((output) => {
        const outputName = getOutputById(project, output.id).name;
        const upsertAnswerOperation: UpsertAnswerOperation = {
          operation: 'UPSERT_ANSWER',
          attributes: {
            frameCount: this.store.getMaxFrameNumber(outputName) + 1,
          },
          outputId: output.id,
        };
        return upsertAnswerOperation;
      });

    return this.persistenceService.enqueue$(
      defer(() => {
        this.submissionLoggingService.updateStep('upsertAnswerStarted');
        return this.annotationResource.uploadAnnotations$(operations);
      }),
      annotationQueueName,
    );
  }

  private updateMetadata$() {
    const duration = this.timerService.elapsed();
    return this.persistenceService.enqueue$(
      this.annotationResource.updateMetadata$({ duration }),
      annotationQueueName,
    );
  }

  private submit$(qaGroupId: string) {
    return this.persistenceService.enqueue$(
      defer(() => from(this.submitTaskService.submit(qaGroupId))),
      annotationQueueName,
    );
  }
}
